import { useState } from "react";
import { useTheme } from "./ThemeContext"; // Dark mode support
import { Helmet } from "react-helmet";

const units = {
    Length: ["Meters", "Kilometers", "Miles", "Feet", "Centimeters", "Inches", "Yards"],
    Weight: ["Kilograms", "Grams", "Pounds", "Ounces", "Tons", "Milligrams"],
    Temperature: ["Celsius", "Fahrenheit", "Kelvin"],
    Volume: ["Liters", "Milliliters", "Cubic Meters", "Cubic Feet", "Gallons"],
    Speed: ["Meters per second", "Kilometers per hour", "Miles per hour", "Knots"],
};

const conversionRates = {
    Meters: 1,
    Kilometers: 0.001,
    Miles: 0.000621371,
    Feet: 3.28084,
    Centimeters: 100,
    Inches: 39.3701,
    Yards: 1.09361,
    Kilograms: 1,
    Grams: 1000,
    Pounds: 2.20462,
    Ounces: 35.274,
    Tons: 0.001,
    Milligrams: 1000000,
    Liters: 1,
    Milliliters: 1000,
    "Cubic Meters": 0.001,
    "Cubic Feet": 0.0353147,
    Gallons: 0.264172,
    "Meters per second": 1,
    "Kilometers per hour": 3.6,
    "Miles per hour": 2.23694,
    Knots: 1.94384
};

export default function UnitConverter() {
    const [category, setCategory] = useState("Length");
    const [fromUnit, setFromUnit] = useState("Meters");
    const [toUnit, setToUnit] = useState("Kilometers");
    const [value, setValue] = useState(1);
    const [result, setResult] = useState(0);
    const { darkMode } = useTheme(); // Get dark mode state

    const handleConvert = () => {
        let numericValue = parseFloat(value) || 0;

        if (category === "Temperature") {
            if (fromUnit === "Celsius" && toUnit === "Fahrenheit") {
                setResult(numericValue * 9 / 5 + 32);
            } else if (fromUnit === "Celsius" && toUnit === "Kelvin") {
                setResult(numericValue + 273.15);
            } else if (fromUnit === "Fahrenheit" && toUnit === "Celsius") {
                setResult((numericValue - 32) * 5 / 9);
            } else if (fromUnit === "Fahrenheit" && toUnit === "Kelvin") {
                setResult((numericValue - 32) * 5 / 9 + 273.15);
            } else if (fromUnit === "Kelvin" && toUnit === "Celsius") {
                setResult(numericValue - 273.15);
            } else if (fromUnit === "Kelvin" && toUnit === "Fahrenheit") {
                setResult((numericValue - 273.15) * 9 / 5 + 32);
            } else {
                setResult(numericValue);
            }
        } else {
            setResult(numericValue * (conversionRates[toUnit] / conversionRates[fromUnit]));
        }
    };

    const schemaOrgJSONLD = {
        "@context": "http://schema.org",
        "@type": "WebPage",
        "name": "Unit Converter - Convert Length, Weight, Temperature, and More",
        "description": "An easy-to-use unit converter for converting between different units such as length, weight, and temperature.",
        "url": window.location.href, // Get the URL dynamically
        "mainEntityOfPage": "True",
    };

    return (

        <>
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(schemaOrgJSONLD)}
                </script>
                <title>Unit Converter - Convert Length, Weight, Temperature, and More</title>
                <meta name="description" content="Convert between different units of measurement including length, weight, temperature, volume, and speed with ease." />
                <meta name="keywords" content="unit converter, length converter, weight converter, temperature converter, speed converter, volume converter" />
                <meta property="og:title" content="Unit Converter - Convert Length, Weight, Temperature, and More" />
                <meta property="og:description" content="An easy-to-use unit converter for converting between various units of measurement like length, weight, and temperature." />
                <meta property="og:image" content="URL_TO_IMAGE" />
            </Helmet>
            <main className={`container mx-auto px-4 pb-12 min-h-screen ${darkMode ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-800"}`}>
                <div className="flex justify-center p-6">
                    <div className={`max-w-md w-full ${darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"} shadow-lg rounded-2xl p-6`}>
                        <h2 className="text-xl font-semibold mb-4">Unit Converter</h2>

                        {/* Category Selector */}
                        <select
                            className={`w-full p-2 border rounded mb-4 ${darkMode ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"}`}
                            value={category}
                            onChange={(e) => {
                                const newCategory = e.target.value;
                                setCategory(newCategory);
                                setFromUnit(units[newCategory][0]);
                                setToUnit(units[newCategory][1]);
                            }}
                        >
                            {Object.keys(units).map((cat) => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>

                        {/* Unit Selection */}
                        <div className="grid grid-cols-2 gap-4">
                            <select className={`p-2 border rounded ${darkMode ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"}`} value={fromUnit} onChange={(e) => setFromUnit(e.target.value)}>
                                {units[category].map((unit) => (
                                    <option key={unit} value={unit}>{unit}</option>
                                ))}
                            </select>
                            <select className={`p-2 border rounded ${darkMode ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"}`} value={toUnit} onChange={(e) => setToUnit(e.target.value)}>
                                {units[category].map((unit) => (
                                    <option key={unit} value={unit}>{unit}</option>
                                ))}
                            </select>
                        </div>

                        {/* Input Field */}
                        <input
                            type="number"
                            className={`w-full p-2 border rounded mt-4 ${darkMode ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"}`}
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                        />

                        {/* Convert Button */}
                        <button
                            className={`mt-4 w-full py-2 rounded-lg transition ${darkMode ? "bg-blue-500 text-white hover:bg-blue-600" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                            onClick={handleConvert}
                        >
                            Convert
                        </button>

                        {/* Result Display */}
                        <div className="mt-4 text-lg">{result} {toUnit}</div>
                    </div>
                </div>
            </main>
        </>



    );
}
